<template>
	<div class="conciliacao">
		<!-- Cabeçalho da página -->
		<v-toolbar color="white" flat elevation="0" class="toolbar-conciliacao mb-4" height="80">
			<v-toolbar-title class="d-flex flex-column justify-center py-2">
				<div class="d-flex align-center">
					<div class="icon-wrapper">
						<v-icon color="#023449" size="26">mdi-bank-check</v-icon>
					</div>
					<div class="ml-3">
						<span class="module-title">Conciliação de boletos</span>
						<p class="subtitle mb-0">Pagamentos de boletos avisados pelo Itaú e a situação de cada título</p>
					</div>
				</div>
			</v-toolbar-title>
			<v-spacer />
			<span v-if="atualizadoEm" class="text-caption grey--text mr-3">Atualizado às {{ atualizadoEm }}</span>
			<v-btn outlined small color="primary" :loading="carregando" @click="carregarTudo">
				<v-icon left small>mdi-refresh</v-icon>Atualizar
			</v-btn>
		</v-toolbar>

		<!-- Filtros -->
		<v-card outlined class="mb-3">
			<div
				class="resumo-cabecalho"
				:class="{ 'resumo-cabecalho-aberto': !filtrosMinimizado }"
				:style="!filtrosMinimizado ? estiloCabecalhoAberto : null"
				role="button"
				tabindex="0"
				:aria-expanded="`${!filtrosMinimizado}`"
				:title="filtrosMinimizado ? 'Clique para expandir os filtros' : 'Clique para minimizar os filtros'"
				@click="alternarFiltros"
				@keyup.enter.self="alternarFiltros"
			>
				<div class="d-flex align-center">
					<v-icon small color="primary" class="mr-2">mdi-filter-variant</v-icon>
					<span class="resumo-titulo">Filtros</span>
				</div>
				<v-spacer />
				<v-chip v-if="filtrosMinimizado" small label outlined class="resumo-periodo">
					<v-icon small left>mdi-store-outline</v-icon>
					{{ filialTexto || "Todas filiais" }}
				</v-chip>
				<v-chip v-if="filtrosMinimizado" small label outlined class="resumo-periodo ml-2">
					<v-icon small left>mdi-calendar-outline</v-icon>
					{{ periodoTexto }}
				</v-chip>
				<v-chip v-if="clienteTexto && filtrosMinimizado" small label outlined class="resumo-periodo ml-2">
					<v-icon small left>mdi-account-outline</v-icon>
					{{ clienteTexto }}
				</v-chip>
				<v-chip v-if="buscaTexto && filtrosMinimizado" small label outlined class="resumo-periodo ml-2">
					<v-icon small left>mdi-pound</v-icon>
					{{ buscaTexto }}
				</v-chip>
				<v-btn icon small class="ml-2" :title="filtrosMinimizado ? 'Expandir os filtros' : 'Minimizar os filtros'" @click.stop="alternarFiltros">
					<v-icon small>{{ filtrosMinimizado ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
				</v-btn>
			</div>
			<v-expand-transition>
				<div v-show="!filtrosMinimizado">
					<v-divider />
					<v-card-text class="py-3">
						<v-row dense align="center">
							<v-col cols="12" md="3">
								<SeletorFiliais
									:value="filiaisSelecionadas"
									:filiais="opcoesFilial"
									label="Filial"
									single
									:shortcuts="[]"
									:clearable="podeFiltrarFilial"
									:disabled="carregando || !podeFiltrarFilial"
									@change="escolherFilial"
									@clear="escolherFilial([])"
								/>
							</v-col>
							<v-col cols="12" md="3">
								<SeletorPeriodo
									:value="{ inicio: busca.dtinicio, fim: busca.dtfim }"
									label="Período do pagamento"
									:clearable="false"
									:disabled="carregando"
									@apply="escolherPeriodo"
								/>
							</v-col>
							<v-col cols="12" md>
								<SeletorCliente v-model="busca.cliente" label="Cliente" placeholder="Todos os clientes" :disabled="carregando" />
							</v-col>
							<v-col cols="12" md="2">
								<v-select
									v-model="busca.origem"
									:items="opcoesOrigem"
									label="Origem do pagamento"
									prepend-inner-icon="mdi-qrcode"
									outlined
									dense
									hide-details
									class="campo-titulo"
									:menu-props="{ offsetY: true }"
									:disabled="carregando"
									@change="carregarTudo"
								/>
							</v-col>
							<v-col cols="12" md="2">
								<v-text-field
									v-model="busca.busca"
									label="Nº título"
									placeholder="Título ou nosso número"
									prepend-inner-icon="mdi-pound"
									clearable
									outlined
									dense
									hide-details
									class="campo-titulo"
									:disabled="carregando"
									@keyup.enter="carregarTudo"
								>
									<template v-slot:append v-if="buscaTexto">
										<v-icon color="success">mdi-check-circle</v-icon>
									</template>
								</v-text-field>
							</v-col>
							<v-col cols="12" md="auto" class="d-flex justify-end">
								<v-btn
									v-if="$vuetify.breakpoint.lgAndUp"
									fab
									color="primary"
									elevation="0"
									class="botao-consultar"
									title="Consultar"
									aria-label="Consultar"
									:loading="carregando"
									@click="carregarTudo"
								>
									<v-icon>mdi-magnify</v-icon>
								</v-btn>
								<v-btn v-else color="primary" elevation="0" :loading="carregando" @click="carregarTudo">Consultar</v-btn>
							</v-col>
						</v-row>
						<div class="texto-sec mt-2">Cada filial mostra os pagamentos a partir da sua data de implantação na conciliação.</div>
					</v-card-text>
				</div>
			</v-expand-transition>
		</v-card>

		<!-- Resumo -->
		<v-card outlined class="mb-3">
			<div
				class="resumo-cabecalho"
				:class="{ 'resumo-cabecalho-aberto': !resumoMinimizado }"
				:style="!resumoMinimizado ? estiloCabecalhoAberto : null"
				role="button"
				tabindex="0"
				:aria-expanded="`${!resumoMinimizado}`"
				:title="resumoMinimizado ? 'Clique para expandir os cards' : 'Clique para minimizar os cards'"
				@click="alternarResumo"
				@keyup.enter.self="alternarResumo"
			>
				<div class="d-flex align-center">
					<v-icon small color="primary" class="mr-2">mdi-cash-check</v-icon>
					<span class="resumo-titulo">Resumo de pagamentos</span>
				</div>
				<v-spacer />
				<div v-if="resumoMinimizado" class="resumo-pilulas">
					<span
						v-for="c in cartoesPagamentos"
						:key="c.chave"
						class="resumo-pilula"
						:class="{ 'resumo-pilula-ativa': cartaoAtivo(c), 'resumo-pilula-alerta': c.alerta && c.qtd > 0 }"
						:style="estiloCartao(c)"
						:title="dicaCartao(c)"
						@click.stop="filtrarCartao(c)"
					>
						<span class="resumo-ponto" :style="{ backgroundColor: corHex(c.cor) }"></span>
						<span class="resumo-pilula-rotulo">{{ c.titulo }}</span>
						<b>{{ c.qtd }}</b>
					</span>
				</div>
				<v-btn icon small class="ml-2" :title="resumoMinimizado ? 'Expandir os cards' : 'Minimizar os cards'" @click.stop="alternarResumo">
					<v-icon small>{{ resumoMinimizado ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
				</v-btn>
			</div>
			<v-expand-transition>
				<div v-show="!resumoMinimizado">
					<v-divider />
					<div class="pa-2">
						<div class="cartoes-grade">
							<div v-for="c in cartoesPagamentos" :key="c.chave">
								<v-tooltip bottom max-width="320" open-delay="300" :disabled="!c.dica">
									<template v-slot:activator="{ on }">
										<div
											class="cartao-pag"
											:class="{ 'cartao-pag-alerta': c.alerta && c.qtd > 0 }"
											:style="estiloCartao(c)"
											v-on="on"
											@click="filtrarCartao(c)"
										>
											<div class="cartao-pag-linha">
												<span class="cartao-pag-icone" :style="{ backgroundColor: corHex(c.cor) + '1f' }">
													<v-icon small :color="corHex(c.cor)">{{ c.icone }}</v-icon>
												</span>
												<div class="cartao-pag-corpo">
													<div class="cartao-pag-titulo">
														{{ c.titulo }}<span v-if="percentual(c)" class="cartao-pag-pct">{{ percentual(c) }}</span>
													</div>
													<div class="cartao-pag-numeros">
														<span class="cartao-pag-qtd" :class="corNumero(c)">{{ c.qtd }}</span>
														<span class="cartao-pag-valor">{{ formataDinheiro(c.valor) }}</span>
													</div>
												</div>
											</div>
										<div v-if="c.subs" class="cartao-sub-acao" @click.stop>
											<span v-if="subAtiva(c)" class="cartao-sub-rotulo" :style="{ color: corHex(c.cor) }">{{ subAtiva(c).rotulo }}</span>
											<v-menu offset-y left>
												<template v-slot:activator="{ on }">
													<v-btn icon small aria-label="Filtrar por tipo" :style="subAtiva(c) ? { backgroundColor: corHex(c.cor) + '26', color: corHex(c.cor) } : null" v-on="on">
														<v-icon small>mdi-filter-variant</v-icon>
													</v-btn>
												</template>
												<v-list dense>
													<v-list-item v-for="x in opcoesSub(c)" :key="x.filtro" @click="escolherSub(c, x.filtro)">
														<v-list-item-icon class="mr-2">
															<v-icon small :color="busca[c.campo] === x.filtro ? corHex(c.cor) : 'grey'">{{ busca[c.campo] === x.filtro ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ x.rotulo }}</v-list-item-title>
														<v-list-item-action-text class="ml-4"><b>{{ x.qtd }}</b></v-list-item-action-text>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
										</div>
									</template>
									<span>{{ c.dica }}</span>
								</v-tooltip>
							</div>
							<div class="cartao-filtro-celula">
								<div class="cartao-filtro">
									<v-icon small :color="filtroAtivoTexto ? 'primary' : 'grey'">mdi-filter-variant</v-icon>
									<div class="cartao-filtro-texto">
										<div class="cartao-pag-titulo">Filtro dos cards</div>
										<div class="cartao-legenda">{{ filtroAtivoTexto || "Nenhum: mostrando todos" }}</div>
									</div>
									<v-btn v-if="filtroAtivoTexto" small text color="primary" @click="limparCartoes">Limpar</v-btn>
								</div>
							</div>
						</div>
					</div>
				</div>
			</v-expand-transition>
		</v-card>

		<!-- Pagamentos -->
		<v-card outlined class="mt-3">
			<div
				class="resumo-cabecalho"
				:class="{ 'resumo-cabecalho-aberto': !pagamentosMinimizado }"
				:style="!pagamentosMinimizado ? estiloCabecalhoAberto : null"
				role="button"
				tabindex="0"
				:aria-expanded="`${!pagamentosMinimizado}`"
				:title="pagamentosMinimizado ? 'Clique para expandir a lista' : 'Clique para minimizar a lista'"
				@click="alternarPagamentos"
				@keyup.enter.self="alternarPagamentos"
			>
				<div class="d-flex align-center">
					<v-icon small color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
					<span class="resumo-titulo">Pagamentos</span>
				</div>
				<v-spacer />
				<div v-if="pagamentosMinimizado" class="resumo-pilulas">
					<span class="resumo-pilula resumo-pilula-info">
						<span class="resumo-pilula-rotulo">Pagamentos</span>
						<b>{{ totaisLista.qtd }}{{ truncado.pagamentos ? "+" : "" }}</b>
					</span>
					<span class="resumo-pilula resumo-pilula-info">
						<span class="resumo-pilula-rotulo">Valor pago</span>
						<b>{{ formataDinheiro(totaisLista.pago) }}</b>
					</span>
				</div>
				<v-btn icon small class="ml-2" :title="pagamentosMinimizado ? 'Expandir a lista' : 'Minimizar a lista'" @click.stop="alternarPagamentos">
					<v-icon small>{{ pagamentosMinimizado ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
				</v-btn>
			</div>
			<v-expand-transition>
				<div v-show="!pagamentosMinimizado">
					<v-divider />
					<v-alert v-if="truncado.pagamentos" dense text type="info" class="ma-4">
						Mostrando os primeiros {{ limite }} pagamentos. Refine o período ou a busca.
					</v-alert>
					<v-data-table
						:headers="colunasPagamentos"
						:items="pagamentos"
						:loading="carregandoLista.pagamentos"
						:items-per-page="50"
						:custom-sort="ordenarPagamentos"
						dense
						loading-text="Carregando..."
						class="tabela-pagamentos"
						:footer-props="rodape"
					>
						<template v-slot:no-data>
							<div class="vazio">
								<v-icon size="40" color="grey lighten-1">mdi-cash-remove</v-icon>
								<div>Nenhum pagamento no período</div>
							</div>
						</template>
						<template v-slot:[`item.pix`]="{ item }">
							<v-tooltip v-if="item.origem === 'PIX'" bottom color="primary" max-width="280">
								<template v-slot:activator="{ on }">
									<v-chip small color="cyan lighten-5" text-color="cyan darken-3" v-on="on">
										<v-icon x-small left>mdi-qrcode</v-icon>QR Code
									</v-chip>
								</template>
								<span>Boleto pago pelo QR Code.</span>
							</v-tooltip>
							<v-tooltip v-else-if="item.origem === 'BOLETO'" bottom color="primary" max-width="280">
								<template v-slot:activator="{ on }">
									<v-chip small color="amber lighten-5" text-color="amber darken-4" v-on="on">
										<v-icon x-small left>mdi-barcode</v-icon>Boleto
									</v-chip>
								</template>
								<span>Pagamento do boleto avisado pelo banco (código de barras).</span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.dtpagamento`]="{ item }">
							<div class="pagamento-data text-no-wrap">{{ item.dtpagamento || "-" }}</div>
							<v-tooltip v-if="horaAviso(item)" bottom max-width="300">
								<template v-slot:activator="{ on }">
									<div class="pagamento-hora text-no-wrap" v-on="on">
										<v-icon x-small>mdi-clock-outline</v-icon>{{ horaAviso(item) }}
									</div>
								</template>
								<span v-if="item.origem === 'PIX'">Horário do Pix informado pelo Itaú (hora de Brasília): {{ item.dtaviso }}.</span>
								<span v-else>Aviso do Itaú: {{ item.dtaviso }}. O banco informa só a data do pagamento; esta é a hora em que o aviso chegou.</span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.filial`]="{ item }">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<span class="filial-id" v-on="on">{{ item.idfilial }}</span>
								</template>
								<span>{{ item.idfilial }} - {{ item.filial }}</span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.nossonumero`]="{ item }">
							<div class="doc-linha mono text-no-wrap">
								<v-tooltip bottom max-width="280">
									<template v-slot:activator="{ on }">
										<v-icon x-small class="doc-icone" v-on="on">mdi-bank-outline</v-icon>
									</template>
									<span>Nosso número: identificador do boleto no Itaú (o que consta no extrato do banco).</span>
								</v-tooltip>
								{{ item.nossonumero || "-" }}
							</div>
							<div class="doc-linha mono text-no-wrap">
								<v-tooltip bottom max-width="280">
									<template v-slot:activator="{ on }">
										<v-icon x-small class="doc-icone" v-on="on">mdi-storefront-outline</v-icon>
									</template>
									<span>Título no CISS: número, dígito e série do título a receber da loja.</span>
								</v-tooltip>
								<template v-if="item.idcliente">{{ item.titulo }}-{{ item.digito }} {{ item.serie }}</template>
								<span v-else class="grey--text">sem título</span>
							</div>
						</template>
						<template v-slot:[`item.cliente`]="{ item }">
							<div v-if="nomeCliente(item)" class="texto-corte" :title="nomeCliente(item)">{{ nomeCliente(item) }}</div>
							<span v-else class="grey--text">-</span>
							<v-tooltip v-if="item.pagadordoc || item.idcliente" bottom max-width="320">
								<template v-slot:activator="{ on }">
									<div class="texto-sec texto-corte" v-on="on">
										{{ formatarDocumento(item.pagadordoc) }}<template v-if="item.idcliente"><template v-if="item.pagadordoc"> · </template>cód. {{ item.idcliente }}</template>
									</div>
								</template>
								<span>
									{{ formatarDocumento(item.pagadordoc) }}<template v-if="item.idcliente"><template v-if="item.pagadordoc"> · </template>cód. {{ item.idcliente }}</template>
								</span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.dtvencimento`]="{ item }">
							<span v-if="item.dtvencimento" class="mono">{{ item.dtvencimento }}</span>
							<span v-else class="grey--text">-</span>
						</template>
						<template v-slot:[`item.valortitulo`]="{ item }">
							<span class="mono">{{ formataDinheiro(item.valortitulo) }}</span>
							<div
								v-if="divergeEmitido(item)"
								class="valor-emitido valor-emitido-linha"
								:title="`O boleto vale ${formataDinheiro(item.valortitulo)} no Itaú, mas foi emitido pelo sistema com ${formataDinheiro(item.valboleto)} (diferença de ${formataDinheiro(Math.abs(item.valboleto - item.valortitulo))}). Ele pode ter sido alterado no banco.`"
							>
								emitido {{ formataDinheiro(item.valboleto) }}
							</div>
						</template>
						<template v-slot:[`item.valorpago`]="{ item }">
							<v-tooltip v-if="juros(item) > 0" bottom>
								<template v-slot:activator="{ on }">
									<div class="juros" v-on="on">
										<v-icon x-small color="orange darken-3">mdi-information-outline</v-icon>
										+ {{ formataDinheiro(juros(item)) }}
									</div>
								</template>
								<span>
									Acréscimo pago acima do valor do título (juros e multa): {{ percentualJuros(item) }}.<br />
									Título {{ formataDinheiro(item.valortitulo) }}, pago {{ formataDinheiro(item.valorpago) }}.
								</span>
							</v-tooltip>
							<strong class="mono">{{ formataDinheiro(item.valorpago) }}</strong>
						</template>
						<template v-slot:[`item.situacao`]="{ item }">
							<v-chip small :color="chipConciliacao(item).fundo" :text-color="chipConciliacao(item).texto_cor" :title="item.situacao === 'CONFERIDO' ? textoConferencia(item) : null">
								<v-icon x-small left>{{ chipConciliacao(item).icone }}</v-icon>{{ chipConciliacao(item).texto }}
							</v-chip>
						</template>
						<template v-slot:[`item.baixa`]="{ item }">
							<v-chip v-if="item.baixa" small :color="chipBaixa(item).fundo" :text-color="chipBaixa(item).texto_cor" :title="chipBaixa(item).dica">
								<v-icon x-small left>{{ chipBaixa(item).icone }}</v-icon>{{ chipBaixa(item).texto }}
							</v-chip>
							<span v-else class="grey--text">-</span>
						</template>
						<template v-slot:[`item.dtbaixaciss`]="{ item }">
							<v-tooltip bottom max-width="280">
								<template v-slot:activator="{ on }">
									<span class="text-no-wrap" v-on="on">{{ item.dtbaixaciss || "-" }}</span>
								</template>
								<span>Data da baixa do título no CISS.</span>
							</v-tooltip>
						</template>
						<template v-slot:[`item.acoes`]="{ item }">
							<v-menu offset-y left>
								<template v-slot:activator="{ on }">
									<v-btn icon small v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item @click="verDetalhes(item)">
										<v-list-item-icon class="mr-3"><v-icon small>mdi-text-box-search-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Ver detalhes</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="permissao('parcelamento_conciliacao_baixar') && podeBaixarLinha(item)" @click="abrirBaixa(item)">
										<v-list-item-icon class="mr-3"><v-icon small>mdi-cash-check</v-icon></v-list-item-icon>
										<v-list-item-title>Baixar título</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="permissao('parcelamento_conciliacao_conferir') && item.situacao === 'DIVERGENTE'" @click="abrirConferencia(item)">
										<v-list-item-icon class="mr-3"><v-icon small>mdi-clipboard-check-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Marcar como conferido</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="permissao('parcelamento_conciliacao_conciliar') && podeVincular(item)" @click="abrirVinculo(item)">
										<v-list-item-icon class="mr-3"><v-icon small>mdi-link-variant</v-icon></v-list-item-icon>
										<v-list-item-title>{{ item.situacao === "DIVERGENTE" ? "Conciliar manualmente" : "Vincular título" }}</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
						<template v-slot:foot>
							<tfoot class="linha-totais">
								<tr>
									<td :colspan="posicaoValores">Total do período: {{ totaisLista.qtd }}{{ truncado.pagamentos ? "+" : "" }} pagamentos</td>
									<td class="text-right text-no-wrap">{{ formataDinheiro(totaisLista.titulo) }}</td>
									<td class="text-right text-no-wrap">
										<div v-if="totaisLista.juros > 0" class="juros" title="Juros e multa somados">+ {{ formataDinheiro(totaisLista.juros) }}</div>
										<strong>{{ formataDinheiro(totaisLista.pago) }}</strong>
									</td>
									<td colspan="5"></td>
								</tr>
							</tfoot>
						</template>
					</v-data-table>
				</div>
			</v-expand-transition>
		</v-card>

		<!-- Detalhes do pagamento -->
		<v-dialog v-model="dialogDetalhes" max-width="760" scrollable>
			<v-card v-if="detalhe">
				<v-card-title class="text-h6">
					Detalhes do pagamento
					<v-spacer />
					<v-btn icon small @click="dialogDetalhes = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-4">
					<v-row dense>
						<v-col v-for="d in camposDetalhe" :key="d.rotulo" :cols="d.largo ? 12 : 6">
							<div class="texto-sec">{{ d.rotulo }}</div>
							<div class="font-weight-medium">{{ d.valor || "-" }}</div>
						</v-col>
					</v-row>

					<v-divider class="my-4" />
					<div class="text-subtitle-2 mb-3">
						<v-icon small left>mdi-calculator-variant-outline</v-icon>Composição do valor
					</div>
					<v-progress-linear v-if="carregandoComposicao" indeterminate class="mb-2" />
					<div v-else-if="!composicao" class="texto-sec">Não foi possível carregar a composição do valor.</div>
					<template v-else>
						<div v-if="composicao.pix" class="comp-grupo">
							<div class="comp-titulo">Pix recebido no Itaú</div>
							<div class="comp-linha">
								<span>Horário do Pix (hora de Brasília)</span>
								<span class="mono">{{ composicao.pix.horario }}</span>
							</div>
							<div class="comp-linha">
								<span>Identificador do Pix (endToEndId)</span>
								<span class="mono texto-corte">{{ composicao.pix.endtoend }}</span>
							</div>
							<div v-if="composicao.pix.txid" class="comp-linha">
								<span>Identificador do boleto no QR Code (txid)</span>
								<span class="mono texto-corte">{{ composicao.pix.txid }}</span>
							</div>
							<div v-if="composicao.pix.valoriginal !== null" class="comp-linha">
								<span>Valor original</span>
								<span class="mono">{{ formataDinheiro(composicao.pix.valoriginal) }}</span>
							</div>
							<div v-if="composicao.pix.valjuros" class="comp-linha">
								<span>Juros</span>
								<span class="mono comp-juros">{{ formataDinheiro(composicao.pix.valjuros) }}</span>
							</div>
							<div v-if="composicao.pix.valmulta" class="comp-linha">
								<span>Multa</span>
								<span class="mono comp-juros">{{ formataDinheiro(composicao.pix.valmulta) }}</span>
							</div>
							<div v-if="composicao.pix.valdesconto" class="comp-linha">
								<span>Desconto</span>
								<span class="mono">{{ formataDinheiro(composicao.pix.valdesconto) }}</span>
							</div>
							<div v-if="composicao.pix.valabatimento" class="comp-linha">
								<span>Abatimento</span>
								<span class="mono">{{ formataDinheiro(composicao.pix.valabatimento) }}</span>
							</div>
							<div v-if="composicao.pix.valdevolvido > 0" class="comp-linha">
								<span>Valor devolvido ao pagador</span>
								<span class="mono comp-juros">{{ formataDinheiro(composicao.pix.valdevolvido) }}</span>
							</div>
						</div>
						<div class="comp-grupo">
							<div class="comp-titulo">Boleto e aviso do Itaú</div>
							<div class="comp-linha">
								<span>Valor do boleto emitido</span>
								<span class="mono">
									<template v-if="composicao.comboleto">{{ formataDinheiro(composicao.valboleto) }}<span class="texto-sec ml-2">vence {{ composicao.dtvencimento }}</span></template>
									<span v-else class="grey--text">boleto não encontrado no cadastro</span>
								</span>
							</div>
							<div class="comp-linha">
								<span>Valor do título informado pelo Itaú</span>
								<span class="mono">{{ formataDinheiro(composicao.valtituloitau) }}</span>
							</div>
							<div class="comp-linha">
								<span>Juros e multa (pago menos título do Itaú)</span>
								<span class="mono" :class="{ 'comp-juros': jurosComposicao > 0 }">{{ formataDinheiro(jurosComposicao) }}</span>
							</div>
							<div class="comp-linha comp-total">
								<span>Valor pago em {{ composicao.dtpagamento }}</span>
								<span class="mono">{{ formataDinheiro(composicao.valpago) }}</span>
							</div>
						</div>
						<div v-if="composicao.comtitulo" class="comp-grupo">
							<div class="comp-titulo">Título no CISS</div>
							<div class="comp-linha">
								<span>Valor original do título</span>
								<span class="mono">{{ formataDinheiro(composicao.valtitulociss) }}</span>
							</div>
							<div v-for="b in composicao.baixas" :key="b.idplanilha" class="comp-linha">
								<span>
									Baixa de {{ b.dtpagamento }}
									<span class="texto-sec">{{ rotuloOrigemBaixa(b.origem) }}, planilha {{ b.idplanilha }}</span>
									<v-chip v-if="b.idplanilha === detalhe.idplanilhabaixa" x-small label class="ml-1">baixa deste pagamento</v-chip>
								</span>
								<span class="mono">{{ formataDinheiro(b.valor) }}<span v-if="b.juros > 0" class="comp-juros ml-2">+ {{ formataDinheiro(b.juros) }} juros</span></span>
							</div>
							<div v-if="!composicao.baixas.length" class="comp-linha">
								<span class="texto-sec">Nenhuma baixa no CISS</span>
								<span></span>
							</div>
							<div v-if="composicao.valdescontociss > 0" class="comp-linha">
								<span>Descontos concedidos</span>
								<span class="mono">{{ formataDinheiro(composicao.valdescontociss) }}</span>
							</div>
							<div class="comp-linha comp-total">
								<span>Saldo atual do título</span>
								<span class="mono">{{ formataDinheiro(composicao.saldociss) }}</span>
							</div>
						</div>
						<div class="comp-grupo">
							<div class="comp-titulo">Conferência</div>
							<div v-for="(m, i) in conferenciasComposicao" :key="i" class="comp-conferencia">
								<v-icon x-small :color="m.cor" class="mr-2">{{ m.icone }}</v-icon>{{ m.texto }}
							</div>
						</div>
					</template>

					<v-divider class="my-4" />
					<div class="text-subtitle-2 mb-3">
						<v-icon small left>mdi-webhook</v-icon>Histórico do pagamento no Itaú
					</div>
					<v-progress-linear v-if="carregandoLogDetalhe" indeterminate class="mb-2" />
					<div v-else-if="!logDetalhe.length" class="texto-sec">Nenhum registro do webhook para este pagamento.</div>
					<v-timeline v-else dense align-top class="timeline-log">
						<v-timeline-item v-for="(l, i) in logDetalhe" :key="i" small :color="corLog(l.tipo)">
							<div class="d-flex justify-space-between flex-wrap">
								<strong>{{ rotuloLog(l.tipo) }}</strong>
								<span class="texto-sec">{{ l.dtlog }}</span>
							</div>
							<div v-if="eventoLog(l)" class="evento-log mt-1">
								<div v-for="linha in linhasEvento(eventoLog(l))" :key="linha.rotulo" class="texto-sec">
									<span>{{ linha.rotulo }}:</span> <strong>{{ linha.valor }}</strong>
								</div>
							</div>
							<div v-else class="texto-sec mt-1">{{ l.mensagem }}</div>
						</v-timeline-item>
					</v-timeline>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Vincular título ao pagamento -->
		<v-dialog v-model="dialogVinculo" max-width="920" scrollable persistent>
			<v-card v-if="vinculo.item">
				<v-card-title class="text-h6">
					{{ vinculo.item.situacao === "DIVERGENTE" ? "Conciliar pagamento divergente" : "Vincular título ao pagamento" }}
					<v-spacer />
					<v-btn icon small :disabled="vinculo.salvando" @click="dialogVinculo = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-4">
					<div class="vinculo-resumo">
						<div>
							<div class="texto-sec">Pagamento</div>
							<div class="mono">{{ vinculo.item.dtpagamento }}</div>
						</div>
						<div>
							<div class="texto-sec">Valor pago</div>
							<div class="mono"><strong>{{ formataDinheiro(vinculo.item.valorpago) }}</strong></div>
							<div v-if="valorReferenciaVinculo !== vinculo.item.valorpago" class="texto-sec mono">título no Itaú {{ formataDinheiro(valorReferenciaVinculo) }}</div>
						</div>
						<div>
							<div class="texto-sec">Vencimento do boleto</div>
							<div class="mono">{{ vinculo.item.dtvencimento || "-" }}</div>
						</div>
						<div>
							<div class="texto-sec">Nosso número</div>
							<div class="mono">{{ vinculo.item.nossonumero || "-" }}</div>
						</div>
						<div class="vinculo-pagador">
							<div class="texto-sec">Pagador</div>
							<div class="texto-corte" :title="nomeCliente(vinculo.item)">{{ nomeCliente(vinculo.item) || "-" }}</div>
							<div class="texto-sec mono">{{ formatarDocumento(vinculo.item.pagadordoc) }}</div>
						</div>
						<div class="vinculo-filial">
							<div class="texto-sec">Filial</div>
							<div>{{ vinculo.item.idfilial }} - {{ vinculo.item.filial }}</div>
						</div>
					</div>

					<div class="texto-sec mt-4">{{ textoSugestoes }}</div>

					<v-progress-linear v-if="vinculo.carregando" indeterminate class="mt-3" />
					<div v-else-if="!vinculo.candidatos.length" class="vazio">
						<v-icon size="36" color="grey lighten-1">mdi-link-variant-off</v-icon>
						<div>Nenhum título compatível encontrado para este pagamento.</div>
					</div>
					<div v-else class="vinculo-lista mt-3">
						<div class="vinculo-cab vinculo-grade">
							<span></span>
							<span>Título</span>
							<span>Vencimento</span>
							<span class="text-right">Valor do título</span>
							<span class="text-right">Saldo</span>
							<span class="text-center" title="Data da baixa do título (último pagamento registrado no contas a receber)">Baixado em</span>
							<span class="text-center" title="Correspondência com o pagamento">Corresp.</span>
							<span>Situação</span>
						</div>
						<div
							v-for="t in vinculo.candidatos"
							:key="chaveCandidato(t)"
							class="vinculo-linha vinculo-grade"
							:class="{ 'vinculo-ativo': vinculo.selecionado === chaveCandidato(t), 'vinculo-desabilitado': !t.mesmovalor }"
							role="radio"
							:aria-checked="`${vinculo.selecionado === chaveCandidato(t)}`"
							@click="selecionarCandidato(t)"
						>
							<v-icon small :color="vinculo.selecionado === chaveCandidato(t) ? 'primary' : ''">
								{{ vinculo.selecionado === chaveCandidato(t) ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
							</v-icon>
							<span class="mono vinculo-titulo" :title="`${t.idcliente} - ${t.cliente}`">{{ t.titulo }}-{{ t.digito }} {{ t.serie }}</span>
							<span class="mono" :title="t.mesmovencimento ? 'Mesmo vencimento do boleto pago' : ''">
								<span :class="{ 'vinculo-confere': t.mesmovencimento }">{{ t.dtvencimento || "-" }}</span>
							</span>
							<span class="mono text-right">
								<span :class="{ 'vinculo-confere': confereValorVinculo(t.valtitulo) }">{{ formataDinheiro(t.valtitulo) }}</span>
							</span>
							<span class="mono text-right">
								<span :class="{ 'vinculo-confere': confereValorVinculo(t.saldo) }">{{ formataDinheiro(t.saldo) }}</span>
							</span>
							<span class="mono text-center" :title="t.dtultimopagamento ? 'Data da baixa do título (último pagamento registrado no contas a receber)' : ''">
								{{ t.baixado && t.dtultimopagamento ? t.dtultimopagamento : "-" }}
							</span>
							<span class="text-center">
								<span class="vinculo-compat" :class="classeCompat(t)" :title="detalheCompat(t)">{{ t.compatibilidade }}%</span>
							</span>
							<span class="vinculo-situacao">
								<v-chip x-small label :color="t.baixado ? 'blue-grey lighten-4' : 'orange lighten-4'">{{ t.baixado ? "baixado" : "em aberto" }}</v-chip>
								<v-chip v-if="t.vencido" x-small label color="red lighten-4" title="Título vencido na data do pagamento">vencido</v-chip>
								<v-chip v-if="t.doboleto" x-small label color="blue lighten-4">título do boleto</v-chip>
								<v-chip
									v-if="t.mesmovalor && !confereValorVinculo(t.valtitulo) && !confereValorVinculo(t.saldo)"
									x-small
									label
									color="green lighten-4"
									title="O valor do pagamento é o saldo que o título tinha na data do pagamento, antes de baixas parciais"
								>
									saldo na data
								</v-chip>
								<v-chip v-if="!t.mesmovalor" x-small label color="orange lighten-4" title="O valor do pagamento não é o valor do título, o saldo nem o saldo na data do pagamento">
									valor difere
								</v-chip>
							</span>
						</div>
					</div>

					<div class="texto-sec mt-2">Só dá para vincular títulos com o mesmo valor do pagamento (valor do título, saldo atual ou saldo na data do pagamento).</div>
					<v-alert v-if="candidatoSelecionado && !candidatoSelecionado.mesmodoc && !candidatoSelecionado.doboleto" dense text type="warning" class="mt-3 mb-0">
						O CPF/CNPJ do cliente do título não é o do pagador do boleto. Confira antes de vincular.
					</v-alert>
					<v-alert v-if="candidatoSelecionado && !candidatoSelecionado.podebaixar" dense text type="blue-grey" class="mt-3 mb-0">
						Só é possível vincular: {{ candidatoSelecionado.motivosembaixa }}.
					</v-alert>

					<v-textarea
						v-model="vinculo.observacao"
						label="Observação (obrigatória)"
						placeholder="Ex.: motivo do boleto ter sido emitido direto pelo Itaú e não pelo Gestor"
						outlined
						dense
						rows="2"
						auto-grow
						counter="280"
						maxlength="280"
						class="mt-4"
						hide-details="auto"
						:disabled="vinculo.salvando"
					/>
				</v-card-text>
				<v-divider />
				<v-card-actions>
					<v-spacer />
					<v-btn text :disabled="vinculo.salvando" @click="dialogVinculo = false">Cancelar</v-btn>
					<v-btn outlined color="primary" :loading="vinculo.salvando" :disabled="!podeConfirmarVinculo" @click="confirmarVinculo(false)">Vincular</v-btn>
					<v-btn
						v-if="permissao('parcelamento_conciliacao_baixar')"
						color="primary"
						elevation="0"
						:disabled="!podeConfirmarVinculo || vinculo.salvando || !candidatoSelecionado.podebaixar"
						@click="abrirConfirmacaoVinculoBaixa"
					>
						Vincular e baixar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Conferência da auditoria -->
		<v-dialog v-model="conferencia.aberto" max-width="560" persistent>
			<v-card v-if="conferencia.item">
				<v-card-title class="text-h6">Conferir pagamento divergente</v-card-title>
				<v-divider />
				<v-card-text class="pt-4">
					<div class="comp-linha"><span>Cliente</span><span class="texto-corte">{{ nomeCliente(conferencia.item) || "-" }}</span></div>
					<div class="comp-linha"><span>Nosso número</span><span class="mono">{{ conferencia.item.nossonumero || "-" }}</span></div>
					<div class="comp-linha"><span>Valor do boleto no Itaú</span><span class="mono">{{ formataDinheiro(conferencia.item.valortitulo) }}</span></div>
					<div class="comp-linha">
						<span>Valor emitido pelo sistema</span>
						<span class="mono" :class="{ 'comp-juros': divergeEmitido(conferencia.item) }">{{ conferencia.item.valboleto > 0 ? formataDinheiro(conferencia.item.valboleto) : "-" }}</span>
					</div>
					<div class="comp-linha"><span>Valor pago</span><span class="mono">{{ formataDinheiro(conferencia.item.valorpago) }}</span></div>
					<v-textarea
						v-model="conferencia.observacao"
						label="Resultado da conferência (obrigatório)"
						placeholder="O que a auditoria verificou e por que a divergência está justificada"
						outlined
						dense
						rows="3"
						auto-grow
						counter="300"
						maxlength="300"
						class="mt-4"
						hide-details="auto"
						:disabled="conferencia.salvando"
					/>
					<div class="texto-sec mt-2">O pagamento passa de Divergente para Conferido, com a sua observação, o seu nome e a data registrados.</div>
				</v-card-text>
				<v-divider />
				<v-card-actions>
					<v-spacer />
					<v-btn text :disabled="conferencia.salvando" @click="conferencia.aberto = false">Cancelar</v-btn>
					<v-btn color="primary" elevation="0" :loading="conferencia.salvando" :disabled="(conferencia.observacao || '').trim().length < 5" @click="confirmarConferencia">Marcar como conferido</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Confirmação da baixa pelo Gestor -->
		<v-dialog v-model="confirmaBaixa.aberto" max-width="520" persistent>
			<v-card>
				<v-card-title class="text-h6">Baixar título pelo Gestor</v-card-title>
				<v-divider />
				<v-card-text class="pt-4">
					<v-progress-linear v-if="confirmaBaixa.carregando" indeterminate />
					<template v-else>
						<div class="comp-linha"><span>Título</span><span class="mono">{{ confirmaBaixa.titulo }}</span></div>
						<div class="comp-linha"><span>Cliente</span><span class="texto-corte">{{ confirmaBaixa.cliente }}</span></div>
						<div class="comp-linha"><span>Valor pago pelo cliente</span><span class="mono">{{ formataDinheiro(confirmaBaixa.valor) }}</span></div>
						<template v-if="confirmaBaixa.calculo">
							<div class="comp-linha"><span>Principal (saldo do título)</span><span class="mono">{{ formataDinheiro(confirmaBaixa.calculo.principal) }}</span></div>
							<div class="comp-linha"><span>Juros cobrado pelo banco</span><span class="mono">{{ formataDinheiro(confirmaBaixa.calculo.jurosbanco) }}</span></div>
						</template>
						<div class="comp-linha"><span>Data do pagamento</span><span class="mono">{{ confirmaBaixa.data }}</span></div>
						<div class="comp-linha"><span>Data da baixa (hoje)</span><span class="mono">{{ dataHoje }}</span></div>
						<div class="comp-linha"><span>Vencimento do título</span><span class="mono">{{ confirmaBaixa.vencimento || "-" }}</span></div>
						<div class="comp-linha"><span>Baixa lançada com o usuário</span><span>{{ usuario && usuario.nome }}</span></div>
						<div v-if="confirmaBaixa.calculo" class="juros-comparacao mt-3">
							<div class="juros-lado">
								<div class="texto-sec">Juros do banco</div>
								<div class="juros-valor mono">{{ formataDinheiro(confirmaBaixa.calculo.jurosbanco) }}</div>
								<div class="texto-sec">{{ detalheJurosBanco }}</div>
							</div>
							<div class="juros-lado">
								<div class="texto-sec">Juros do CISS</div>
								<div class="juros-valor mono">{{ confirmaBaixa.calculo.juroscisscalculado ? formataDinheiro(confirmaBaixa.calculo.jurosciss) : "-" }}</div>
								<div class="texto-sec">{{ confirmaBaixa.calculo.juroscisscalculado ? "0,25% ao dia composto, sem multa" : "não foi possível calcular" }}</div>
							</div>
						</div>
						<div v-if="confirmaBaixa.calculo && confirmaBaixa.calculo.juroscisscalculado" class="texto-sec mt-2">{{ conclusaoJuros }}</div>
						<v-alert v-if="!confirmaBaixa.pode" dense text type="warning" class="mt-3 mb-0">Não é possível baixar: {{ confirmaBaixa.motivo }}.</v-alert>
						<div v-else class="texto-sec mt-3">
							A baixa grava no CISS os mesmos lançamentos da tela de recebimentos (baixa, forma de pagamento, caixa e contábil), pelo valor que o banco recebeu. Não pode ser desfeita por aqui.
						</div>
					</template>
				</v-card-text>
				<v-divider />
				<v-card-actions>
					<v-spacer />
					<v-btn text :disabled="confirmaBaixa.salvando" @click="confirmaBaixa.aberto = false">Cancelar</v-btn>
					<v-btn color="primary" elevation="0" :loading="confirmaBaixa.salvando" :disabled="confirmaBaixa.carregando || !confirmaBaixa.pode" @click="executarBaixa">Baixar título</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="snackbar.ativo" :color="snackbar.cor" :timeout="snackbar.acao ? 9000 : 5000">
			{{ snackbar.texto }}
			<template v-slot:action="{ attrs }">
				<v-btn v-if="snackbar.acao" text v-bind="attrs" @click="snackbar.acao(); snackbar.ativo = false">{{ snackbar.acaoTexto }}</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import SeletorCliente from "@/Components/UI/Inputs/SeletorCliente.vue";
import SeletorFiliais from "@/Components/UI/Inputs/SeletorFiliais.vue";
import SeletorPeriodo from "@/Components/UI/Inputs/SeletorPeriodo.vue";
import mixinFilial from "@/util/mixinFilial";
import corrigirAcentos from "@/util/corrigirAcentos";

const LIMITE = 500;
// filiais que não aparecem no filtro (além dos depósitos)
const FILIAIS_FORA_DO_FILTRO = [4, 6, 13, 28, 33, 39, 40, 44];

const CORES = { green: "#4caf50", grey: "#9e9e9e", red: "#f44336", orange: "#fb8c00", teal: "#009688", "blue-grey": "#607d8b", indigo: "#3f51b5" };

function iso(d) {
	const m = `${d.getMonth() + 1}`.padStart(2, "0");
	const dia = `${d.getDate()}`.padStart(2, "0");
	return `${d.getFullYear()}-${m}-${dia}`;
}

const CHAVE_RESUMO_MINIMIZADO = "conciliacao_resumo_minimizado";
const CHAVE_FILTROS_MINIMIZADO = "conciliacao_filtros_minimizado";
const CHAVE_PAGAMENTOS_MINIMIZADO = "conciliacao_pagamentos_minimizado";

// preferências só deste navegador; sem armazenamento, a seção abre expandida
function lerMinimizado(chave) {
	try {
		return window.localStorage.getItem(chave) === "1";
	} catch (e) {
		return false;
	}
}

function gravarMinimizado(chave, minimizado) {
	try {
		window.localStorage.setItem(chave, minimizado ? "1" : "0");
	} catch (e) {
		// sem armazenamento, vale só nesta tela
	}
}

function periodoPadrao() {
	const hoje = iso(new Date());
	return { dtinicio: hoje, dtfim: hoje };
}

export default {
	name: "ParcelamentoConciliacao",
	components: { SeletorCliente, SeletorFiliais, SeletorPeriodo },
	mixins: [mixinFilial],
	data() {
		return {
			resumoMinimizado: lerMinimizado(CHAVE_RESUMO_MINIMIZADO),
			filtrosMinimizado: lerMinimizado(CHAVE_FILTROS_MINIMIZADO),
			pagamentosMinimizado: lerMinimizado(CHAVE_PAGAMENTOS_MINIMIZADO),
			carregando: false,
			carregandoLista: { pagamentos: false },
			limite: LIMITE,
			atualizadoEm: "",
			busca: Object.assign({ idfilial: null, cliente: null, busca: "", situacao: null, baixa: null, origem: "" }, periodoPadrao()),
			opcoesOrigem: [
				{ text: "Todas", value: "" },
				{ text: "Boleto (aviso do banco)", value: "BOLETO" },
				{ text: "QR Code", value: "PIX" },
			],
			dialogDetalhes: false,
			detalhe: null,
			logDetalhe: [],
			carregandoLogDetalhe: false,
			composicao: null,
			confirmaBaixa: { aberto: false, origem: "", item: null, titulo: "", cliente: "", valor: 0, data: "", vencimento: "", calculo: null, pode: false, motivo: "", carregando: false, salvando: false },
			conferencia: { aberto: false, item: null, observacao: "", salvando: false },
			dialogVinculo: false,
			vinculo: { item: null, candidatos: [], selecionado: null, observacao: "", carregando: false, salvando: false },
			carregandoComposicao: false,
			rodape: { "items-per-page-text": "Linhas por página", "items-per-page-options": [50, 100], "page-text": "{0}-{1} de {2}", "show-first-last-page": true },
			resumo: null,
			pagamentos: [],
			truncado: { pagamentos: false },
			snackbar: { ativo: false, cor: "error", texto: "", acaoTexto: "", acao: null },
		};
	},
	computed: {
		...mapState(["backendUrl", "usuario"]),
		podeFiltrarFilial() {
			return this.permissao("parcelamento_conciliacao_filtro_empresa");
		},
		opcoesFilial() {
			// depósitos fora do filtro
			return (this.filiais.lista || []).filter((f) => !f.deposito && !FILIAIS_FORA_DO_FILTRO.includes(f.idfilial)).map((f) => {
				const filial = corrigirAcentos(f.filial);
				return { ...f, filial, texto: `${f.idfilial} - ${filial}` };
			});
		},
		filiaisSelecionadas() {
			return this.busca.idfilial ? [this.busca.idfilial] : [];
		},
		mostrarFilial() {
			return this.podeFiltrarFilial && this.opcoesFilial.length > 1;
		},
		// cabeçalho expandido: fundo suave na cor primária e faixa à esquerda
		estiloCabecalhoAberto() {
			const cor = this.corHex("primary");
			return { backgroundColor: `${cor}14`, boxShadow: `inset 4px 0 0 ${cor}` };
		},
		filialTexto() {
			const f = this.opcoesFilial.find((o) => o.idfilial === this.busca.idfilial);
			return f ? f.texto : "";
		},
		clienteTexto() {
			const nome = this.busca.cliente ? this.busca.cliente.nome || `Cliente #${this.busca.cliente.idcliente}` : "";
			return nome.length > 28 ? `${nome.slice(0, 28)}…` : nome;
		},
		buscaTexto() {
			const b = (this.busca.busca || "").trim();
			return b.length > 24 ? `${b.slice(0, 24)}…` : b;
		},
		periodoTexto() {
			const f = (d) => (d ? d.split("-").reverse().join("/") : "");
			if (this.busca.dtinicio === this.busca.dtfim) return f(this.busca.dtinicio);
			return `${f(this.busca.dtinicio)} a ${f(this.busca.dtfim)}`;
		},
		textoSugestoes() {
			return this.vinculo.item && this.vinculo.item.pagadordoc
				? "Títulos do mesmo CPF/CNPJ do pagador, na filial do pagamento, em ordem de correspondência."
				: "Títulos da filial com vencimento próximo ou de mesmo valor, em ordem de correspondência (o pagamento não tem CPF/CNPJ).";
		},
		// valor do título no Itaú (ou o pago) para comparar com título e saldo
		valorReferenciaVinculo() {
			const i = this.vinculo.item;
			if (!i) return 0;
			return i.valortitulo > 0 ? i.valortitulo : i.valorpago;
		},
		candidatoSelecionado() {
			return this.vinculo.candidatos.find((t) => this.chaveCandidato(t) === this.vinculo.selecionado) || null;
		},
		dataHoje() {
			return new Date().toLocaleDateString("pt-BR");
		},
		detalheJurosBanco() {
			const c = this.confirmaBaixa.calculo;
			if (!c || c.jurosbanco <= 0) return "sem acréscimo";
			const confere = c.jurosbancocalculado > 0 && Math.abs(c.jurosbancocalculado - c.jurosbanco) <= 0.01;
			return confere ? `2% de multa + 0,25% ao dia, ${c.diasatraso} dias de atraso` : "valor cobrado pelo banco";
		},
		conclusaoJuros() {
			const c = this.confirmaBaixa.calculo;
			if (!c) return "";
			const dif = Math.round((c.jurosbanco - c.jurosciss) * 100) / 100;
			if (dif > 0) return `O banco cobrou ${this.formataDinheiro(dif)} a mais que o CISS. A baixa grava o juros cobrado pelo banco.`;
			if (dif < 0) return `O CISS cobraria ${this.formataDinheiro(-dif)} a mais que o banco. Esse valor fica registrado como juros isentado, sem alterar o saldo.`;
			return "Os juros do banco e do CISS conferem.";
		},
		podeConfirmarVinculo() {
			return !!this.candidatoSelecionado && (this.vinculo.observacao || "").trim().length >= 5;
		},
		jurosComposicao() {
			const c = this.composicao;
			return c ? Math.max(0, Math.round((c.valpago - c.valtituloitau) * 100) / 100) : 0;
		},
		// cruza boleto emitido, aviso do Itaú e baixas do título
		conferenciasComposicao() {
			const c = this.composicao;
			if (!c) return [];
			const r = [];
			const aviso = (texto) => r.push({ cor: "orange darken-2", icone: "mdi-alert-outline", texto });
			const ok = (texto) => r.push({ cor: "green darken-1", icone: "mdi-check-circle-outline", texto });
			const info = (texto) => r.push({ cor: "blue darken-1", icone: "mdi-information-outline", texto });
			const din = (v) => this.formataDinheiro(v);
			if (!c.comboleto) {
				info("O nosso número não existe no cadastro de boletos do sistema, costuma ser boleto emitido por outro canal.");
				return r;
			}
			if (c.idfilialboleto && c.idfilialboleto !== c.idfilial) {
				aviso(`O boleto é da filial ${c.idfilialboleto}, mas o aviso foi recebido pela credencial da filial ${c.idfilial}.`);
			}
			const dif = Math.round((c.valboleto - c.valtituloitau) * 100) / 100;
			if (Math.abs(dif) > 0.009) {
				aviso(`O boleto foi emitido pelo sistema com ${din(c.valboleto)}, mas vale ${din(c.valtituloitau)} no Itaú (diferença de ${din(Math.abs(dif))}). Ele pode ter sido alterado no banco.`);
				if (c.baixasanteriores > 0 && Math.abs(c.valboleto - c.baixasanteriores - c.valtituloitau) < 0.01) {
					info(`A diferença é igual às baixas do título feitas antes deste pagamento (${din(c.baixasanteriores)}): o Itaú informou o saldo em aberto na data.`);
				}
			} else {
				ok("O valor emitido confere com o valor do título informado pelo Itaú.");
			}
			if (this.jurosComposicao > 0) {
				info(`Foi pago ${din(this.jurosComposicao)} acima do título (juros e multa).`);
			}
			if (c.comtitulo && c.saldociss < -0.009) {
				aviso(`O título tem baixas acima do seu valor (saldo ${din(c.saldociss)}).`);
			}
			return r;
		},
		camposDetalhe() {
			const p = this.detalhe;
			if (!p) return [];
			const conc = this.chipConciliacao(p);
			const baixa = p.baixa ? this.chipBaixa(p) : null;
			return [
				{ rotulo: "Filial", valor: p.idfilial ? `${p.idfilial} - ${p.filial}` : "", largo: true },
				{ rotulo: "Pagamento", valor: p.dtpagamento },
				{ rotulo: "Crédito", valor: p.creditoconfirmado ? p.dtcredito : "A confirmar (o Itaú ainda não enviou a liquidação)" },
				{ rotulo: "Aviso do Itaú", valor: p.dtaviso },
				{ rotulo: "Nosso número", valor: p.nossonumero },
				{ rotulo: "Pagador", valor: p.pagador, largo: true },
				{ rotulo: "Documento do pagador", valor: this.formatarDocumento(p.pagadordoc) },
				{ rotulo: "Vencimento do boleto", valor: p.dtvencimento },
				{ rotulo: "Valor do título", valor: this.formataDinheiro(p.valortitulo) },
				{ rotulo: "Valor pago", valor: this.formataDinheiro(p.valorpago) },
				{ rotulo: "Juros e multa", valor: this.juros(p) > 0 ? this.formataDinheiro(this.juros(p)) : "" },
				{ rotulo: "Cliente", valor: p.idcliente ? `${p.idcliente} - ${p.cliente}` : "", largo: true },
				{ rotulo: "Título", valor: p.titulo ? `${p.titulo}-${p.digito} ${p.serie}` : "" },
				{ rotulo: "Conciliação", valor: conc.texto },
				{ rotulo: "Baixa", valor: baixa ? baixa.texto : "" },
				{ rotulo: "Baixa no CISS em", valor: p.dtbaixaciss },
				{ rotulo: "Baixa feita em", valor: p.baixa === "AUTOMATICA" && p.dtbaixa ? `${p.dtbaixa}${p.idplanilhabaixa ? ` (planilha ${p.idplanilhabaixa})` : ""}` : "" },
				{ rotulo: "Conciliado à mão em", valor: p.dtconciliacao },
				{ rotulo: "Conferido pela auditoria em", valor: p.dtconferencia ? `${p.dtconferencia}${p.usuarioconferencia ? `, ${p.usuarioconferencia}` : ""}` : "" },
				{ rotulo: "Resultado da conferência", valor: p.obsconferencia, largo: true },
				{ rotulo: "Observação", valor: p.observacao, largo: true },
			];
		},
		colunasPagamentos() {
			// colunas fixas; só o Cliente se ajusta
			const c = [
				{ text: "Pagamento", value: "dtpagamento", width: 96, cellClass: "text-no-wrap" },
				{ text: "Filial", value: "filial", width: 64, cellClass: "text-no-wrap" },
				{ text: "Nº Docs", value: "nossonumero", width: 140, cellClass: "text-no-wrap" },
				{ text: "Cliente", value: "cliente", width: "100%", class: "col-cliente", cellClass: "col-corte col-cliente" },
				{ text: "Vencimento", value: "dtvencimento", width: 92, cellClass: "text-no-wrap" },
				{ text: "Valor boleto", value: "valortitulo", align: "end", width: 96, cellClass: "text-no-wrap" },
				{ text: "Valor pago", value: "valorpago", align: "end", width: 104, cellClass: "text-no-wrap" },
				{ text: "Origem", value: "pix", width: 104, class: "col-origem", cellClass: "text-no-wrap col-origem" },
				{ text: "Conciliação", value: "situacao", width: 136, cellClass: "text-no-wrap" },
				{ text: "Baixa", value: "baixa", width: 148, cellClass: "text-no-wrap" },
				{ text: "Baixa em", value: "dtbaixaciss", width: 92, cellClass: "text-no-wrap" },
				{ text: "", value: "acoes", sortable: false, align: "end", width: 44 },
			];
			return this.mostrarFilial ? c : c.filter((x) => x.value !== "filial");
		},
		// colunas antes do Valor do boleto, para o total ficar sob os valores
		posicaoValores() {
			return this.colunasPagamentos.findIndex((c) => c.value === "valortitulo");
		},
		// soma da lista carregada (parcial se foi cortada)
		totaisLista() {
			const r = { qtd: this.pagamentos.length, titulo: 0, juros: 0, pago: 0 };
			this.pagamentos.forEach((p) => {
				r.titulo += p.valortitulo || 0;
				r.pago += p.valorpago || 0;
				r.juros += this.juros(p);
			});
			return r;
		},
		totaisPagamentos() {
			const r = { RECEBIDO: { qtd: 0, valor: 0 }, SEM_TITULO: { qtd: 0, valor: 0 }, DIVERGENTE: { qtd: 0, valor: 0 }, CONCILIADO: { qtd: 0, valor: 0 }, CONFERIDO: { qtd: 0, valor: 0 } };
			((this.resumo && this.resumo.pagamentos) || []).forEach((s) => {
				if (r[s.situacao]) r[s.situacao] = { qtd: s.qtd, valor: s.valor };
			});
			return r;
		},
		totaisBaixas() {
			const r = { AGUARDANDO: { qtd: 0, valor: 0 }, AUTOMATICA: { qtd: 0, valor: 0 }, MANUAL: { qtd: 0, valor: 0 } };
			((this.resumo && this.resumo.baixas) || []).forEach((s) => {
				if (r[s.situacao]) r[s.situacao] = { qtd: s.qtd, valor: s.valor };
			});
			return r;
		},
		// texto do filtro aplicado pelos cards, com o segundo nível quando houver
		filtroAtivoTexto() {
			const partes = [];
			this.cartoesPagamentos.forEach((c) => {
				if (!c.campo || !this.cartaoAtivo(c)) return;
				const sub = (c.subs || []).find((x) => x.filtro === this.busca[c.campo]);
				partes.push(sub ? `${c.titulo} (${sub.rotulo})` : c.titulo);
			});
			return partes.join(" + ");
		},
		// cada card filtra por conciliação (situacao) ou baixa
		cartoesPagamentos() {
			const t = this.totaisPagamentos;
			const b = this.totaisBaixas;
			const total = Object.values(t).reduce((a, s) => ({ qtd: a.qtd + s.qtd, valor: a.valor + s.valor }), { qtd: 0, valor: 0 });
			return [
				{ chave: "total", titulo: "Recebidos", icone: "mdi-cash-multiple", qtd: total.qtd, valor: total.valor, cor: "primary", dica: "Todos os pagamentos do período. Clique para limpar o filtro dos cards." },
				{ chave: "conciliados", titulo: "Conciliados", icone: "mdi-link-variant", qtd: t.RECEBIDO.qtd + t.CONCILIADO.qtd, valor: t.RECEBIDO.valor + t.CONCILIADO.valor, cor: "green", campo: "situacao", filtro: "CONCILIADOS", subs: [{ rotulo: "Automático", filtro: "RECEBIDO", qtd: t.RECEBIDO.qtd }, { rotulo: "Manual", filtro: "CONCILIADO", qtd: t.CONCILIADO.qtd }], dica: "Pagamento ligado a um título, automático ou manual." },
				{ chave: "nao", titulo: "Não conciliados", icone: "mdi-link-variant-off", qtd: t.SEM_TITULO.qtd, valor: t.SEM_TITULO.valor, cor: "grey", campo: "situacao", filtro: "SEM_TITULO", dica: "Título não encontrado: costuma ser boleto emitido por outro canal." },
				{ chave: "div", titulo: "Divergentes", icone: "mdi-alert-circle-outline", qtd: t.DIVERGENTE.qtd, valor: t.DIVERGENTE.valor, cor: "red", alerta: true, campo: "situacao", filtro: "DIVERGENTE", dica: `Título encontrado, mas o valor ou a filial não conferem. Pendentes de conferência da auditoria.` },
				{ chave: "conferidos", titulo: "Conferidos", icone: "mdi-clipboard-check-outline", qtd: t.CONFERIDO.qtd, valor: t.CONFERIDO.valor, cor: "indigo", campo: "situacao", filtro: "CONFERIDO", dica: "Divergências já conferidas pela auditoria, com a observação registrada." },
				{ chave: "aguardando", titulo: "Aguardando baixa", icone: "mdi-clock-outline", qtd: b.AGUARDANDO.qtd, valor: b.AGUARDANDO.valor, cor: "blue", campo: "baixa", filtro: "AGUARDANDO", dica: "Pagamento conciliado com o título ainda em aberto no CISS." },
				{ chave: "baixados", titulo: "Baixados", icone: "mdi-check-circle-outline", qtd: b.AUTOMATICA.qtd + b.MANUAL.qtd, valor: b.AUTOMATICA.valor + b.MANUAL.valor, cor: "teal", campo: "baixa", filtro: "BAIXADOS", subs: [{ rotulo: "Automática", filtro: "AUTOMATICA", qtd: b.AUTOMATICA.qtd }, { rotulo: "Manual", filtro: "MANUAL", qtd: b.MANUAL.qtd }], dica: `Título baixado: ${b.AUTOMATICA.qtd} pelo sistema (baixa automática) e ${b.MANUAL.qtd} por operador (baixa manual).` },
			];
		},
	},
	methods: {
		corpo(extra) {
			return Object.assign(
				{
					dtinicio: this.busca.dtinicio,
					dtfim: this.busca.dtfim,
					idfilial: this.busca.idfilial || null,
					busca: this.busca.busca || "",
					idcliente: this.busca.cliente ? this.busca.cliente.idcliente : null,
					origem: this.busca.origem || "",
				},
				extra || {}
			);
		},
		// só o card selecionado ganha contorno e fundo
		estiloCartao(c) {
			const cor = this.corHex(c.cor);
			const base = { "--cor": cor };
			if (!this.cartaoAtivo(c)) return base;
			return Object.assign(base, {
				borderColor: cor,
				backgroundColor: `${cor}0d`,
				boxShadow: `0 0 0 1px ${cor} inset`,
			});
		},
		dicaCartao(c) {
			return c.dica ? `${this.formataDinheiro(c.valor)} · ${c.dica}` : this.formataDinheiro(c.valor);
		},
		cartaoAtivo(c) {
			if (!c.campo) return false;
			const atual = this.busca[c.campo];
			return atual === c.filtro || (c.subs || []).some((x) => x.filtro === atual);
		},
		// percentual do card sobre o total recebido
		percentual(c) {
			const total = this.cartoesPagamentos[0].qtd;
			return c.campo === "situacao" && total > 0 ? `${Math.round((c.qtd * 100) / total)}%` : "";
		},
		corNumero(c) {
			return c.alerta && c.qtd > 0 ? `${c.cor}--text text--darken-2` : "";
		},
		alternarResumo() {
			this.resumoMinimizado = !this.resumoMinimizado;
			gravarMinimizado(CHAVE_RESUMO_MINIMIZADO, this.resumoMinimizado);
		},
		// o Itaú só informa a data; a hora é a do aviso
		horaAviso(item) {
			const m = /(\d{2}:\d{2})$/.exec(item.dtaviso || "");
			return m ? m[1] : "";
		},
		// valor usado na ordenação: datas dd/mm/aaaa viram aaaammdd (e o pagamento leva a hora junto)
		valorOrdenacao(item, chave) {
			const data = (d) => (d ? d.split("/").reverse().join("") : "");
			switch (chave) {
				case "dtpagamento":
					return data(item.dtpagamento) ? data(item.dtpagamento) + this.horaAviso(item) : "";
				case "dtvencimento":
				case "dtbaixaciss":
					return data(item[chave]);
				case "filial":
					return item.idfilial;
				case "pix":
					return item.origem || "";
				default:
					return item[chave];
			}
		},
		// ordena a lista carregada; vazio vai sempre para o fim
		ordenarPagamentos(items, sortBy, sortDesc) {
			if (!sortBy || !sortBy.length) return items;
			const chave = sortBy[0];
			const sinal = sortDesc[0] ? -1 : 1;
			const vazio = (v) => v === "" || v === null || v === undefined;
			return items.slice().sort((a, b) => {
				const va = this.valorOrdenacao(a, chave);
				const vb = this.valorOrdenacao(b, chave);
				if (vazio(va) || vazio(vb)) return vazio(va) && vazio(vb) ? 0 : vazio(va) ? 1 : -1;
				if (typeof va === "number" && typeof vb === "number") return (va - vb) * sinal;
				return String(va).localeCompare(String(vb), "pt-BR") * sinal;
			});
		},
		alternarPagamentos() {
			this.pagamentosMinimizado = !this.pagamentosMinimizado;
			gravarMinimizado(CHAVE_PAGAMENTOS_MINIMIZADO, this.pagamentosMinimizado);
		},
		alternarFiltros() {
			this.filtrosMinimizado = !this.filtrosMinimizado;
			gravarMinimizado(CHAVE_FILTROS_MINIMIZADO, this.filtrosMinimizado);
		},
		escolherFilial(ids) {
			this.busca.idfilial = ids && ids.length ? ids[0] : null;
		},
		escolherPeriodo(periodo) {
			const padrao = periodoPadrao();
			this.busca.dtinicio = (periodo && periodo.inicio) || padrao.dtinicio;
			this.busca.dtfim = (periodo && periodo.fim) || padrao.dtfim;
			this.carregarTudo();
		},
		divergeEmitido(item) {
			return item.valboleto > 0 && Math.abs(item.valboleto - (item.valortitulo || 0)) > 0.009;
		},
		rotuloOrigemBaixa(origem) {
			return origem === "CAI" ? "caixa" : origem || "origem não informada";
		},
		// cor do selo: verde alta, laranja média, vermelha baixa
		classeCompat(t) {
			if (t.compatibilidade >= 80) return "vinculo-compat-alta";
			if (t.compatibilidade >= 50) return "vinculo-compat-media";
			return "vinculo-compat-baixa";
		},
		// detalhe da nota: valor, vencimento, CPF/CNPJ e título em aberto
		detalheCompat(t) {
			const sim = (ok) => (ok ? "confere" : "não confere");
			const linhas = [`Valor: ${sim(t.mesmovalor)} (50)`];
			if (this.vinculo.item && this.vinculo.item.dtvencimento) linhas.push(`Vencimento: ${sim(t.mesmovencimento)} (25)`);
			if (this.vinculo.item && this.vinculo.item.pagadordoc) linhas.push(`CPF/CNPJ: ${sim(t.mesmodoc)} (15)`);
			linhas.push(`Título em aberto: ${t.baixado ? "não" : "sim"} (10)`);
			return `Correspondência com o pagamento: ${t.compatibilidade}%. ${linhas.join(" · ")}`;
		},
		confereValorVinculo(valor) {
			return Math.abs((valor || 0) - this.valorReferenciaVinculo) <= 0.01;
		},
		selecionarCandidato(t) {
			if (t.mesmovalor) this.vinculo.selecionado = this.chaveCandidato(t);
		},
		chaveCandidato(t) {
			return `${t.idfilial}|${t.idcliente}|${t.titulo}|${t.digito}|${t.serie}`;
		},
		abrirVinculo(item) {
			this.vinculo = { item, candidatos: [], selecionado: null, observacao: "", carregando: false, salvando: false };
			this.dialogVinculo = true;
			this.buscarCandidatos();
		},
		buscarCandidatos() {
			this.vinculo.carregando = true;
			this.vinculo.selecionado = null;
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-candidatos`, { idpagamento: this.vinculo.item.idpagamento })
				.then((res) => {
					this.vinculo.candidatos = res.data.lista || [];
					// divergente com título próprio: já vem escolhido
					const proprio = this.vinculo.candidatos.filter((t) => t.doboleto);
					if (proprio.length === 1) this.vinculo.selecionado = this.chaveCandidato(proprio[0]);
				})
				.catch((e) => {
					this.vinculo.candidatos = [];
					this.erro(e, "Não foi possível buscar os títulos.");
				})
				.finally(() => {
					this.vinculo.carregando = false;
				});
		},
		textoConferencia(item) {
			return `Conferido${item.dtconferencia ? ` em ${item.dtconferencia}` : ""}${item.usuarioconferencia ? ` por ${item.usuarioconferencia}` : ""}: ${item.obsconferencia || ""}`;
		},
		abrirConferencia(item) {
			this.conferencia = { aberto: true, item, observacao: "", salvando: false };
		},
		confirmarConferencia() {
			this.conferencia.salvando = true;
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-conferir`, {
					idpagamento: this.conferencia.item.idpagamento,
					observacao: this.conferencia.observacao.trim(),
				})
				.then(() => {
					this.conferencia.aberto = false;
					this.avisar("Pagamento marcado como conferido.", "success", "Ver conferidos", this.verConferidos);
					this.carregarTudo();
				})
				.catch((e) => this.erro(e, "Não foi possível registrar a conferência."))
				.finally(() => {
					this.conferencia.salvando = false;
				});
		},
		podeBaixarLinha(item) {
			return (item.situacao === "RECEBIDO" || item.situacao === "CONCILIADO") && item.baixa === "AGUARDANDO";
		},
		// confirma a baixa pelo menu da linha
		abrirBaixa(item) {
			this.confirmaBaixa = {
				aberto: true,
				origem: "menu",
				item,
				titulo: `${item.titulo}-${item.digito} ${item.serie}`.trim(),
				cliente: this.nomeCliente(item),
				valor: item.valorpago,
				data: item.dtpagamento,
				vencimento: "",
				calculo: null,
				pode: false,
				motivo: "",
				carregando: true,
				salvando: false,
			};
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-composicao`, { idpagamento: item.idpagamento })
				.then((res) => {
					this.confirmaBaixa.pode = !!res.data.podebaixargestor;
					this.confirmaBaixa.motivo = res.data.motivosembaixa || "";
					this.confirmaBaixa.vencimento = res.data.vencimentotitulo || "";
					this.confirmaBaixa.calculo = res.data.baixacalculo || null;
				})
				.catch((e) => {
					this.confirmaBaixa.motivo = "não foi possível conferir o título";
					this.erro(e, "Não foi possível conferir o título.");
				})
				.finally(() => {
					this.confirmaBaixa.carregando = false;
				});
		},
		// confirma vincular e baixar pelo dialog de vínculo
		abrirConfirmacaoVinculoBaixa() {
			const t = this.candidatoSelecionado;
			if (!t) return;
			this.confirmaBaixa = {
				aberto: true,
				origem: "vinculo",
				item: this.vinculo.item,
				titulo: `${t.titulo}-${t.digito} ${t.serie}`.trim(),
				cliente: `${t.idcliente} - ${t.cliente}`,
				valor: this.vinculo.item.valorpago,
				data: this.vinculo.item.dtpagamento,
				vencimento: t.dtvencimento,
				calculo: t.baixacalculo || null,
				pode: !!t.podebaixar,
				motivo: t.motivosembaixa || "",
				carregando: false,
				salvando: false,
			};
		},
		executarBaixa() {
			if (this.confirmaBaixa.origem === "vinculo") {
				return this.confirmarVinculo(true);
			}
			this.confirmaBaixa.salvando = true;
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-baixar`, { idpagamento: this.confirmaBaixa.item.idpagamento })
				.then((res) => {
					this.confirmaBaixa.aberto = false;
					this.avisar(`Título ${this.confirmaBaixa.titulo} baixado (planilha ${res.data.idplanilha}).`, "success");
					this.carregarTudo();
				})
				.catch((e) => this.erro(e, "Não foi possível baixar o título."))
				.finally(() => {
					this.confirmaBaixa.salvando = false;
				});
		},
		confirmarVinculo(baixar) {
			const t = this.candidatoSelecionado;
			if (!t || !this.podeConfirmarVinculo) return;
			this.vinculo.salvando = true;
			if (baixar) this.confirmaBaixa.salvando = true;
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-vincular`, {
					idpagamento: this.vinculo.item.idpagamento,
					idfilial: t.idfilial,
					idcliente: t.idcliente,
					titulo: t.titulo,
					digito: t.digito,
					serie: t.serie,
					observacao: this.vinculo.observacao.trim(),
					baixar: !!baixar,
				})
				.then(() => {
					this.dialogVinculo = false;
					this.confirmaBaixa.aberto = false;
					this.avisar(baixar ? `Pagamento ligado ao título ${t.titulo}-${t.digito} e título baixado.` : `Pagamento ligado ao título ${t.titulo}-${t.digito}.`, "success");
					this.carregarTudo();
				})
				.catch((e) => this.erro(e, baixar ? "Não foi possível vincular e baixar o título." : "Não foi possível vincular o título."))
				.finally(() => {
					this.vinculo.salvando = false;
					this.confirmaBaixa.salvando = false;
				});
		},
		verDetalhes(item) {
			this.detalhe = item;
			this.dialogDetalhes = true;
			this.logDetalhe = [];
			this.composicao = null;
			this.carregandoLogDetalhe = true;
			this.carregandoComposicao = true;
			axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-composicao`, { idpagamento: item.idpagamento })
				.then((res) => {
					this.composicao = res.data;
				})
				.catch((e) => this.erro(e, "Não foi possível carregar a composição do valor."))
				.finally(() => {
					this.carregandoComposicao = false;
				});
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamento-log`, { idpagamento: item.idpagamento })
				.then((res) => {
					this.logDetalhe = res.data.lista || [];
				})
				.catch((e) => this.erro(e, "Não foi possível carregar o histórico do webhook."))
				.finally(() => {
					this.carregandoLogDetalhe = false;
				});
		},
		rotuloLog(tipo) {
			const r = {
				webhook_evento: "Aviso recebido do Itaú",
				webhook_informativo: "Baixa operacional registrada",
				webhook_simulado: "Registrado, sem baixa",
				webhook_baixa: "Baixa automática efetivada",
				webhook_recusado: "Recusado pelo sistema",
				webhook_erro: "Erro no processamento",
				conciliacao_vinculo: "Vínculo manual",
				conciliacao_baixa: "Baixa pelo Gestor",
				conciliacao_conferencia: "Divergência conferida",
				pix_evento: "Pix de boleto registrado",
				pix_baixa: "Baixa automática do Pix",
				pix_divergencia: "Pix com divergência",
				pix_devolucao: "Pix com devolução",
			};
			return r[tipo] || tipo;
		},
		corLog(tipo) {
			const c = { webhook_evento: "blue", webhook_informativo: "blue-grey", webhook_simulado: "teal", webhook_baixa: "green", webhook_recusado: "orange", webhook_erro: "red", conciliacao_vinculo: "teal", conciliacao_baixa: "green", conciliacao_conferencia: "indigo", pix_evento: "teal", pix_divergencia: "orange", pix_devolucao: "red" };
			return c[tipo] || "grey";
		},
		eventoLog(l) {
			if (l.tipo !== "webhook_evento") return null;
			try {
				const o = JSON.parse(l.mensagem);
				return o && typeof o === "object" ? o : null;
			} catch (e) {
				return null;
			}
		},
		linhasEvento(o) {
			const tipos = { "06": "Baixa efetiva (06)", "95": "Baixa operacional (95)" };
			const data = (d) => (d ? d.split("-").reverse().join("/") : "");
			const linhas = [
				{ rotulo: "Tipo de liquidação", valor: tipos[o.tipoLiquidacao] || o.tipoLiquidacao },
				{ rotulo: "Valor do título", valor: o.valorTitulo ? this.formataDinheiro(o.valorTitulo) : "" },
				{ rotulo: "Valor pago", valor: o.valorPagoTotalCobranca ? this.formataDinheiro(o.valorPagoTotalCobranca) : "" },
				{ rotulo: "Data do pagamento", valor: data(o.dataInclusaoPagamento) },
				{ rotulo: "Data do crédito", valor: data(o.dataCredito) },
				{ rotulo: "Beneficiário", valor: o.idBeneficiario },
				{ rotulo: "Identificador do boleto", valor: o.idBoleto },
			];
			return linhas.filter((l) => l.valor);
		},
		podeVincular(item) {
			return item.situacao === "SEM_TITULO" || item.situacao === "DIVERGENTE";
		},
		percentualJuros(item) {
			return item.valortitulo > 0 ? `${((this.juros(item) / item.valortitulo) * 100).toFixed(2).replace(".", ",")}% sobre o título` : "";
		},
		corHex(cor) {
			if (cor === "primary") {
				const t = this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.currentTheme;
				return (t && t.primary) || "#1976d2";
			}
			return CORES[cor] || "#9e9e9e";
		},
		avisar(texto, cor, acaoTexto, acao) {
			this.snackbar = { ativo: true, cor: cor || "error", texto, acaoTexto: acaoTexto || "", acao: acao || null };
		},
		erro(e, padrao) {
			this.avisar(this.erroFormatar(e) || padrao);
		},
		carregarResumo() {
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/resumo`, this.corpo())
				.then((res) => {
					this.resumo = res.data;
				})
				.catch((e) => this.erro(e, "Não foi possível carregar o resumo."));
		},
		carregarPagamentos() {
			this.carregandoLista.pagamentos = true;
			return axios
				.post(`${this.backendUrl}boleto/conciliacao/pagamentos`, this.corpo({ situacao: this.busca.situacao || "", baixa: this.busca.baixa || "" }))
				.then((res) => {
					this.pagamentos = res.data.lista || [];
					this.truncado.pagamentos = !!res.data.truncado;
				})
				.catch((e) => this.erro(e, "Não foi possível carregar os pagamentos."))
				.finally(() => {
					this.carregandoLista.pagamentos = false;
				});
		},
		async carregarTudo() {
			this.carregando = true;
			await Promise.all([this.carregarResumo(), this.carregarPagamentos()]);
			this.carregando = false;
			this.atualizadoEm = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
		},
		// clicar no card ativo tira o filtro; conciliação e baixa podem valer juntos
		filtrarCartao(c) {
			if (!c.campo) {
				this.busca.situacao = null;
				this.busca.baixa = null;
			} else {
				this.busca[c.campo] = this.busca[c.campo] === c.filtro ? null : c.filtro;
			}
			this.carregarPagamentos();
		},
		limparCartoes() {
			this.busca.situacao = null;
			this.busca.baixa = null;
			this.carregarPagamentos();
		},
		// segundo nível do card (menu do filtro): o grupo inteiro ou só um tipo
		opcoesSub(c) {
			return [{ rotulo: "Todos", filtro: c.filtro, qtd: c.qtd }, ...(c.subs || [])];
		},
		// tipo selecionado no menu, se houver
		subAtiva(c) {
			return (c.subs || []).find((x) => x.filtro === this.busca[c.campo]) || null;
		},
		escolherSub(c, filtro) {
			this.busca[c.campo] = filtro;
			this.carregarPagamentos();
		},
		// atalho do aviso de conferência
		verConferidos() {
			this.busca.situacao = "CONFERIDO";
			this.busca.baixa = null;
			this.carregarPagamentos();
		},
		// nome: do cliente no CISS quando há título, senão o do Itaú
		nomeCliente(item) {
			return item.cliente || item.pagador || "";
		},
		formatarDocumento(doc) {
			const d = `${doc || ""}`.replace(/\D/g, "");
			if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
			if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
			return doc;
		},
		juros(item) {
			const j = (item.valorpago || 0) - (item.valortitulo || 0);
			return item.valortitulo > 0 && j > 0.009 ? j : 0;
		},
		// situação da conciliação (vínculo com o título)
		chipConciliacao(item) {
			switch (item.situacao) {
				case "SEM_TITULO":
					return { fundo: "blue-grey lighten-5", texto_cor: "blue-grey darken-3", texto: "Não conciliado", icone: "mdi-link-variant-off" };
				case "DIVERGENTE":
					return { fundo: "red lighten-5", texto_cor: "red darken-3", texto: "Divergente", icone: "mdi-alert-circle-outline" };
				case "CONCILIADO":
					return { fundo: "teal lighten-5", texto_cor: "teal darken-3", texto: "Conciliação manual", icone: "mdi-account-check-outline" };
				case "CONFERIDO":
					return { fundo: "indigo lighten-5", texto_cor: "indigo darken-3", texto: "Conferido", icone: "mdi-clipboard-check-outline" };
				default:
					return { fundo: "green lighten-5", texto_cor: "green darken-3", texto: "Conciliado", icone: "mdi-link-variant" };
			}
		},
		// situação da baixa do título
		chipBaixa(item) {
			switch (item.baixa) {
				case "SEM_VINCULO":
					return { fundo: "grey lighten-3", texto_cor: "grey darken-2", texto: "Sem vínculo", icone: "mdi-link-variant-off", dica: "Pagamento sem título ligado, não há o que baixar" };
				case "AUTOMATICA":
					return { fundo: "green lighten-5", texto_cor: "green darken-3", texto: "Baixa automática", icone: "mdi-robot-outline", dica: item.dtbaixa ? `Baixado pelo sistema em ${item.dtbaixa}` : "Baixado pelo sistema" };
				case "MANUAL":
					return { fundo: "blue-grey lighten-5", texto_cor: "blue-grey darken-3", texto: "Baixa manual", icone: "mdi-account-check-outline", dica: "Título baixado no CISS por um operador" };
				default:
					return { fundo: "blue lighten-5", texto_cor: "blue darken-3", texto: "Aguardando baixa", icone: "mdi-clock-outline", dica: "Título ainda em aberto no CISS" };
			}
		},
	},
	created() {
		// sem permissão de filtro, vale a filial do usuário
		if (!this.podeFiltrarFilial) this.busca.idfilial = this.usuario.idfilial || null;
		this.carregarTudo();
	},
};
</script>

<style scoped>
/* cabeçalho da página, borda como a dos cards */
.toolbar-conciliacao {
	/* borda dos cards (v-card outlined) */
	border: thin solid rgba(0, 0, 0, 0.12) !important;
	border-radius: 4px !important;
	box-shadow: none !important;
}
.icon-wrapper {
	background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
	border-radius: 12px;
	padding: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.module-title {
	font-size: 1.25rem;
	font-weight: 700;
	color: #023449;
	line-height: 1.3;
	letter-spacing: -0.02em;
}
.subtitle {
	font-size: 0.813rem;
	color: #64748b;
	margin-top: 2px;
	line-height: 1.2;
	font-weight: 400;
	white-space: normal;
}
@media (max-width: 960px) {
	.toolbar-conciliacao {
		height: auto !important;
		min-height: 80px;
		padding: 12px 16px !important;
	}
	.toolbar-conciliacao >>> .v-toolbar__content {
		flex-wrap: wrap;
		gap: 8px;
		height: auto !important;
	}
	.module-title {
		font-size: 1.125rem;
	}
}
@media (max-width: 600px) {
	.module-title {
		font-size: 1rem;
	}
	.subtitle {
		font-size: 0.75rem;
	}
	.icon-wrapper {
		padding: 8px;
	}
}

/* botão redondo da altura dos campos (40px) */
.botao-consultar.v-btn {
	width: 40px !important;
	height: 40px !important;
	min-width: 0 !important;
	position: relative;
	top: -2px;
}

/* campos Nº título e Origem no visual dos seletores */
.campo-titulo >>> .v-input__control {
	min-height: 44px !important;
}
.campo-titulo >>> fieldset {
	border-color: #cbd5e1;
	border-width: 2px;
	transition: all 0.2s ease;
}
.campo-titulo >>> .v-input__slot {
	background: #f8fafc;
	transition: all 0.2s ease;
	border-radius: 10px;
}
.campo-titulo >>> .v-input__prepend-inner .v-icon {
	color: #023449 !important;
	opacity: 0.7;
	transition: opacity 0.2s ease;
}
.campo-titulo:hover >>> .v-input__prepend-inner .v-icon {
	opacity: 1 !important;
}
.campo-titulo >>> .v-input__slot:hover {
	background: #ffffff;
}
.campo-titulo >>> .v-input__slot:hover fieldset {
	border-color: #023449;
}
.campo-titulo >>> .v-input--is-focused fieldset {
	border-color: #023449 !important;
	border-width: 2px;
	box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}
.campo-titulo >>> .v-input--is-focused .v-input__slot {
	background: #ffffff !important;
}

.resumo-cabecalho {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.15s;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 8px;
	padding: 8px 12px;
	min-height: 48px;
}
.resumo-cabecalho:hover {
	background-color: rgba(0, 0, 0, 0.02);
}
.resumo-cabecalho-aberto {
	border-radius: 3px 3px 0 0;
}
.resumo-cabecalho-aberto:hover {
	filter: brightness(0.97);
}
.resumo-cabecalho-aberto .v-chip {
	background-color: #fff !important;
}
.resumo-titulo {
	font-size: 0.9375rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.82);
}
.resumo-pilulas {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
}
.resumo-pilula {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 3px 12px 3px 10px;
	font-size: 0.8125rem;
	color: rgba(0, 0, 0, 0.72);
	background: #fff;
	border: 1px solid rgba(0, 0, 0, 0.14);
	border-radius: 999px;
	cursor: pointer;
	transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.resumo-pilula b {
	font-size: 0.875rem;
	color: rgba(0, 0, 0, 0.87);
}
.resumo-pilula:hover {
	background-color: rgba(0, 0, 0, 0.05);
	border-color: rgba(0, 0, 0, 0.32);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
/* pílula selecionada com a cor do card */
.resumo-pilula-ativa {
	font-weight: 500;
}
/* alerta: só o número vermelho */
.resumo-pilula-info,
.resumo-pilula-info:hover {
	cursor: inherit;
	background: #fff;
	border-color: rgba(0, 0, 0, 0.14);
	box-shadow: none;
}
.resumo-pilula-alerta b {
	color: #c62828;
}
.resumo-ponto {
	width: 8px;
	height: 8px;
	border-radius: 50%;
}
/* cards do mesmo tamanho: 1 coluna no celular, 2, 4 (a célula do filtro completa) e 7 numa linha só em tela larga */
.cartoes-grade {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 6px;
}
.cartoes-grade > div {
	display: flex;
	min-width: 0;
}
.cartoes-grade > div > .v-tooltip {
	display: contents;
}
@media (min-width: 720px) {
	.cartoes-grade {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
@media (min-width: 1100px) {
	.cartoes-grade {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
}
@media (min-width: 1560px) {
	.cartoes-grade {
		grid-template-columns: repeat(7, minmax(0, 1fr));
	}
	.cartao-filtro-celula,
	.cartao-sub-rotulo {
		display: none !important;
	}
	.cartoes-grade .cartao-pag {
		gap: 4px;
		padding: 8px 6px 8px 10px;
	}
	.cartoes-grade .cartao-pag-linha {
		gap: 8px;
	}
	.cartoes-grade .cartao-pag-icone {
		width: 28px;
		height: 28px;
	}
}
.cartao-legenda {
	flex: 0 1 auto;
	min-width: 0;
	justify-content: flex-end;
	height: 22px;
	display: flex;
	align-items: center;
	font-size: 0.6875rem;
	font-weight: 500;
	letter-spacing: 0.2px;
	color: rgba(0, 0, 0, 0.5);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.cartao-filtro {
	display: flex;
	flex: 1;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px dashed rgba(0, 0, 0, 0.2);
	border-radius: 8px;
	min-width: 0;
}
.cartao-filtro-texto {
	flex: 1;
	min-width: 0;
}
.cartao-filtro-texto .cartao-legenda {
	margin-top: 2px;
	height: auto;
	justify-content: flex-start;
}
.cartao-sub-acao {
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	gap: 6px;
}
.cartao-sub-rotulo {
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.2px;
}
.cartao-pag {
	position: relative;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 8px 12px 10px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-left: 4px solid var(--cor, rgba(0, 0, 0, 0.12));
	border-radius: 8px;
	cursor: pointer;
	flex: 1;
	overflow: hidden;
	min-width: 0;
	transition: box-shadow 0.15s, border-color 0.15s, background-color 0.15s;
}
/* alerta: ponto vermelho no canto */
.cartao-pag-alerta::after {
	content: "";
	position: absolute;
	top: 8px;
	right: 8px;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #f44336;
}
.cartao-pag:hover {
	border-color: rgba(0, 0, 0, 0.3);
	box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
}
.cartao-pag-icone {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	width: 30px;
	height: 30px;
	border-radius: 50%;
}
.cartao-pag-linha {
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	gap: 10px;
	min-width: 0;
}
.cartao-pag-corpo {
	min-width: 0;
}
.cartao-pag-titulo {
	white-space: nowrap;
	font-size: 0.8125rem;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: 0.1px;
	color: rgba(0, 0, 0, 0.72);
}
.cartao-pag-pct {
	margin-left: 6px;
	font-size: 0.6875rem;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.45);
}
.cartao-pag-numeros {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 0 8px;
}
.cartao-pag-qtd {
	font-size: 1.25rem;
	font-weight: 700;
	line-height: 1.2;
	font-variant-numeric: tabular-nums;
	color: var(--cor, rgba(0, 0, 0, 0.87));
}
.cartao-pag-valor {
	font-size: 0.75rem;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
	color: rgba(0, 0, 0, 0.55);
}
.v-chip.resumo-periodo {
	height: 26px;
	font-size: 0.8125rem;
}
/* linhas de altura única, nomes cortados com tooltip */
.tabela-pagamentos >>> td {
	height: 56px;
}
.tabela-pagamentos >>> td.col-corte {
	max-width: 0;
}
/* o Cliente nunca fica menor que isto; abaixo, a tabela rola */
.tabela-pagamentos >>> td.col-cliente,
.tabela-pagamentos >>> th.col-cliente {
	min-width: 140px;
}
.texto-corte {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
/* corpo em fonte de máquina, cabeçalho na padrão */
.tabela-pagamentos >>> td {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.tabela-pagamentos >>> td {
	font-size: 0.8125rem !important;
}
.tabela-pagamentos >>> td .texto-sec {
	font-size: 0.75rem;
}
/* cabeçalho sem quebra e colunas justas */
.tabela-pagamentos >>> th {
	white-space: nowrap;
}
.tabela-pagamentos >>> th,
.tabela-pagamentos >>> td {
	padding-left: 6px !important;
	padding-right: 6px !important;
}
/* respiro entre o Valor pago e a Origem */
.tabela-pagamentos >>> td.col-origem,
.tabela-pagamentos >>> th.col-origem {
	padding-left: 16px !important;
}
/* seta de ordenação menor, para o cabeçalho não alargar a coluna */
.tabela-pagamentos >>> th .v-data-table-header__icon {
	font-size: 14px !important;
	margin: 0 2px;
}
/* chips da tabela mais justos para caberem todas as colunas */
.tabela-pagamentos >>> td .v-chip.v-size--small {
	padding: 0 11px;
	font-size: 0.75rem;
}
.tabela-pagamentos >>> .v-data-footer {
	margin-right: 0;
	padding: 0 8px;
	min-height: 36px;
	border-top: 1px solid rgba(0, 0, 0, 0.08);
	background: #f8fafc;
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.75rem;
}
.tabela-pagamentos >>> .v-data-footer .v-select__selection,
.tabela-pagamentos >>> .v-data-footer .v-select__selections {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.75rem;
}
.tabela-pagamentos >>> .v-data-footer__select {
	margin-right: 8px;
}
.tabela-pagamentos >>> .v-data-footer__select .v-select {
	margin: 0 0 0 8px;
	padding-top: 0;
}
.tabela-pagamentos >>> .v-data-footer__pagination {
	margin: 0 8px;
	font-weight: 500;
}
.tabela-pagamentos >>> .v-data-footer__icons-before .v-btn,
.tabela-pagamentos >>> .v-data-footer__icons-after .v-btn {
	width: 28px;
	height: 28px;
	border-radius: 6px;
}
.tabela-pagamentos >>> .linha-totais td {
	height: 40px;
	border-top: 2px solid rgba(0, 0, 0, 0.12);
	background: #f1f5f9;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
}
.vinculo-resumo {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 10px 16px;
	padding: 12px;
	border-radius: 6px;
	background: #f8fafc;
	border: 1px solid rgba(0, 0, 0, 0.08);
	font-size: 0.875rem;
}
.vinculo-pagador,
.vinculo-filial {
	grid-column: span 2;
	min-width: 0;
}
.vinculo-lista {
	max-height: 210px;
	overflow-y: auto;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 4px;
}
.vinculo-grade {
	display: grid;
	grid-template-columns: 24px minmax(100px, 1.1fr) 88px 104px 104px 90px 66px minmax(120px, 1.2fr);
	align-items: center;
	gap: 8px;
	padding: 0 8px;
}
.vinculo-cab {
	position: sticky;
	top: 0;
	z-index: 1;
	min-height: 32px;
	background: #f1f5f9;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: rgba(0, 0, 0, 0.6);
}
.vinculo-linha {
	min-height: 44px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
	font-size: 0.8125rem;
	cursor: pointer;
}
.vinculo-linha:hover {
	background-color: rgba(0, 0, 0, 0.03);
}
.vinculo-ativo,
.vinculo-ativo:hover {
	background-color: rgba(2, 52, 73, 0.08);
}
.vinculo-desabilitado,
.vinculo-desabilitado:hover {
	cursor: not-allowed;
	background-color: transparent;
	opacity: 0.55;
}
.vinculo-titulo {
	font-weight: 600;
	white-space: nowrap;
}
.vinculo-confere {
	padding: 1px 5px;
	border-radius: 4px;
	background: #c8e6c9;
	color: #1b5e20;
	font-weight: 600;
}
.vinculo-situacao {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}
.vinculo-compat {
	display: inline-block;
	min-width: 46px;
	padding: 2px 6px;
	border-radius: 4px;
	text-align: center;
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.75rem;
	font-weight: 600;
	cursor: help;
}
.vinculo-compat-alta {
	background: #c8e6c9;
	color: #1b5e20;
}
.vinculo-compat-media {
	background: #ffe0b2;
	color: #e65100;
}
.vinculo-compat-baixa {
	background: #ffcdd2;
	color: #b71c1c;
}
.filial-id {
	cursor: help;
}
.valor-emitido {
	color: #bf360c;
	font-weight: 600;
	cursor: help;
}
.valor-emitido-linha {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.75rem;
	font-weight: 400;
}
.comp-grupo {
	margin-bottom: 12px;
}
.comp-titulo {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: rgba(0, 0, 0, 0.55);
	margin-bottom: 4px;
}
.comp-linha {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 16px;
	padding: 4px 0;
	font-size: 0.875rem;
	border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
}
.juros-comparacao {
	display: flex;
	gap: 12px;
}
.juros-lado {
	flex: 1;
	padding: 8px 12px;
	border: 1px solid rgba(128, 128, 128, 0.35);
	border-radius: 6px;
}
.juros-valor {
	font-size: 1.1rem;
	font-weight: 600;
}
.comp-total {
	font-weight: 600;
	border-bottom: 0;
}
.comp-juros {
	color: #bf360c;
}
.comp-conferencia {
	display: flex;
	align-items: flex-start;
	font-size: 0.8125rem;
	line-height: 1.4;
	padding: 2px 0;
}
.pagamento-data {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.8125rem;
	font-weight: 600;
	line-height: 1.3;
	color: rgba(0, 0, 0, 0.87);
}
.pagamento-hora {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	display: flex;
	align-items: center;
	gap: 3px;
	font-size: 0.75rem;
	line-height: 1.3;
	color: rgba(0, 0, 0, 0.55);
	cursor: help;
}
.texto-sec {
	font-size: 0.8125rem;
	line-height: 1.3;
	color: rgba(0, 0, 0, 0.68);
}
.juros {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	display: flex;
	align-items: center;
	gap: 2px;
	font-size: 0.8125rem;
	color: #bf360c;
	cursor: help;
	white-space: nowrap;
	justify-content: flex-end;
}
.timeline-log {
	padding-top: 0;
}
.evento-log {
	background: rgba(0, 0, 0, 0.03);
	border-radius: 4px;
	padding: 6px 10px;
}
.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.8125rem;
}
.doc-linha {
	display: flex;
	align-items: center;
	gap: 6px;
	line-height: 1.5;
}
.doc-icone {
	color: rgba(0, 0, 0, 0.5) !important;
	cursor: help;
}
.vazio {
	padding: 32px 0;
	text-align: center;
	color: rgba(0, 0, 0, 0.5);
}
</style>
